import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";

import { Card, Divider, Grid, IconButton, Switch, FormControlLabel, Tooltip } from "@mui/material";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import PageTitle from "examples/NewDesign/PageTitle";
import MDBox from "components/MDBox";
import DataTable from "examples/Tables/DataTable";
import CustomButton from "examples/NewDesign/CustomButton";
import BasicButton from "examples/NewDesign/CustomButton/BasicButton";
import NewProject from "examples/modal/NewProject/NewProject";
import DeleteModal from "examples/modal/deleteModal/deleteModal";
import FTextField from "components/Form/FTextField";

import { openSnackbar } from "redux/Slice/Notification";
import {
  createDprKpi,
  deleteDprKpi,
  listDprKpis,
  updateDprKpi,
  updateDprKpiStatus,
} from "redux/Thunks/DprKpi";

import Constants, {
  ButtonTitles,
  Colors,
  Common,
  Icons,
  ModalContent,
  PageTitles,
  defaultData,
} from "utils/Constants";
import pxToRem from "assets/theme/functions/pxToRem";
import { toSnakeCase } from "utils/methods/methods";

const initialFormState = {
  name: "",
  sortOrder: "",
  isActive: true,
};

function DprKpiConfiguration() {
  const dispatch = useDispatch();

  const [kpiList, setKpiList] = useState([]);
  const [loading, setLoading] = useState(Constants.PENDING);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalType, setModalType] = useState(Common.NEW);
  const [form, setForm] = useState(initialFormState);
  const [errors, setErrors] = useState({});
  const [editingId, setEditingId] = useState(null);
  const [deleteData, setDeleteData] = useState({ open: false, id: null });

  const fetchKpis = useCallback(async () => {
    setLoading(Constants.PENDING);
    const res = await dispatch(listDprKpis("includeInactive=true"));

    if (res?.payload?.status === Common.API_STATUS_200) {
      setKpiList(res.payload?.data?.data || []);
      setLoading(Constants.FULFILLED);
    } else {
      setLoading(Constants.REJECTED);
      dispatch(
        openSnackbar({
          message: res?.payload?.data?.message || Constants.SOMETHING_WENT_WRONG,
          notificationType: Constants.NOTIFICATION_ERROR,
        })
      );
    }
  }, [dispatch]);

  const handleReload = async () => {
    await fetchKpis();
  };

  useEffect(() => {
    fetchKpis();
  }, [fetchKpis]);

  const handleOpenModal = (kpi = null) => {
    if (kpi) {
      setForm({
        name: kpi.name || "",
        sortOrder: kpi.sortOrder ?? "",
        isActive: kpi.isActive ?? true,
      });
      setEditingId(kpi[Constants.MONGOOSE_ID]);
      setModalType(Common.UPDATE);
    } else {
      setForm(initialFormState);
      setEditingId(null);
      setModalType(Common.NEW);
    }
    setErrors({});
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setErrors({});
    setForm(initialFormState);
    setEditingId(null);
  };

  const validate = () => {
    const validationErrors = {};
    if (!form.name?.trim()) validationErrors.name = Constants.PLEASE_ENTER_KPI_NAME;
    setErrors(validationErrors);
    return Object.keys(validationErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    const code = toSnakeCase(form.name);
    const payload = {
      ...form,
      code,
      name: form.name.trim(),
      sortOrder: Number(form.sortOrder || 0),
    };

    let res;
    if (modalType === Common.UPDATE && editingId) {
      res = await dispatch(updateDprKpi({ id: editingId, body: payload }));
    } else {
      res = await dispatch(createDprKpi(payload));
    }

    if (
      res?.payload?.status === Common.API_STATUS_200 ||
      res?.payload?.status === Common.API_STATUS_201
    ) {
      dispatch(
        openSnackbar({
          message: res?.payload?.data?.message || Constants.SOMETHING_WENT_WRONG,
          notificationType: Constants.NOTIFICATION_SUCCESS,
        })
      );
      handleCloseModal();
      fetchKpis();
    } else {
      dispatch(
        openSnackbar({
          message: res?.payload?.data?.message || Constants.SOMETHING_WENT_WRONG,
          notificationType: Constants.NOTIFICATION_ERROR,
        })
      );
    }
  };

  const handleStatusToggle = useCallback(
    async (kpiId, isActive) => {
      const res = await dispatch(updateDprKpiStatus({ id: kpiId, isActive }));
      if (res?.payload?.status === Common.API_STATUS_200) {
        setKpiList((prev) =>
          prev.map((kpi) => (kpi[Constants.MONGOOSE_ID] === kpiId ? { ...kpi, isActive } : kpi))
        );
        dispatch(
          openSnackbar({
            message: Constants.STATUS_UPDATE_SUCCESS,
            notificationType: Constants.NOTIFICATION_SUCCESS,
          })
        );
      } else {
        dispatch(
          openSnackbar({
            message: res?.payload?.data?.message || Constants.SOMETHING_WENT_WRONG,
            notificationType: Constants.NOTIFICATION_ERROR,
          })
        );
      }
    },
    [dispatch]
  );

  const handleOpenDeleteModal = useCallback(
    (kpiId) => setDeleteData({ open: true, id: kpiId }),
    []
  );

  const handleCloseDeleteModal = useCallback(() => setDeleteData({ open: false, id: null }), []);

  const handleDeleteKpi = useCallback(async () => {
    if (!deleteData.id) {
      handleCloseDeleteModal();
      return;
    }

    const res = await dispatch(deleteDprKpi(deleteData.id));

    if (res?.payload?.status === Common.API_STATUS_200) {
      setKpiList((prev) => prev.filter((kpi) => kpi[Constants.MONGOOSE_ID] !== deleteData.id));
      dispatch(
        openSnackbar({
          message: res?.payload?.data?.message || Constants.SOMETHING_WENT_WRONG,
          notificationType: Constants.NOTIFICATION_SUCCESS,
        })
      );
    } else {
      dispatch(
        openSnackbar({
          message: res?.payload?.data?.message || Constants.SOMETHING_WENT_WRONG,
          notificationType: Constants.NOTIFICATION_ERROR,
        })
      );
    }
    handleCloseDeleteModal();
  }, [deleteData.id, dispatch, handleCloseDeleteModal]);

  const columns = useMemo(
    () => [
      { Header: "No.", accessor: "srNo", width: "5%" },
      { Header: "Name", accessor: "name", width: "40%" },
      { Header: "Sort Order", accessor: "sortOrder", width: "10%" },
      { Header: "Status", accessor: "status", width: "20%" },
      { Header: "Actions", accessor: "actions", width: "10%" },
    ],
    []
  );

  const rows = useMemo(
    () =>
      (kpiList || []).map((kpi, index) => ({
        srNo: index + 1,
        name: kpi.name,
        sortOrder: kpi.sortOrder ?? "-",
        status: (
          <Switch
            color="info"
            checked={kpi.isActive}
            onChange={(event) =>
              handleStatusToggle(kpi[Constants.MONGOOSE_ID], event.target.checked)
            }
          />
        ),
        actions: (
          <MDBox display="flex" alignItems="center">
            <Tooltip title={Constants.EDIT_KPI_TOOLTIP}>
              <IconButton color="info" onClick={() => handleOpenModal(kpi)} sx={{ mr: 1 }}>
                {Icons.EDIT}
              </IconButton>
            </Tooltip>
            <Tooltip title={Constants.DELETE_KPI_TOOLTIP}>
              <IconButton
                color="info"
                onClick={() => handleOpenDeleteModal(kpi[Constants.MONGOOSE_ID])}
              >
                {Icons.DELETE}
              </IconButton>
            </Tooltip>
          </MDBox>
        ),
      })),
    [kpiList]
  );

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox>
        <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={2} mt={1}>
          <PageTitle title={PageTitles.DPR_KPI_CONFIGURATION} />
          <MDBox display="flex" flexDirection="row" alignItems="center">
            <CustomButton
              title={ButtonTitles.ADD_KPI}
              icon="add_circle_outline"
              background="#191A51"
              color="#ffffff"
              openModal={() => handleOpenModal()}
            />
            <Divider
              orientation="vertical"
              sx={{
                backgroundColor: "var(--gray-300, #D0D5DD)",
                height: "auto",
                marginLeft: pxToRem(16),
                marginRight: 0,
              }}
            />
            <BasicButton
              icon={Icons.RELOAD}
              background={Colors.WHITE}
              border
              color={Colors.BLACK}
              action={handleReload}
              tooltipLabel="Reload"
              style={{ btnMarginLeft: pxToRem(16) }}
            />
          </MDBox>
        </MDBox>
        <Card>
          <MDBox>
            <DataTable
              table={{ columns, rows }}
              isSorted={false}
              entriesPerPage={{ defaultValue: defaultData.MAX_PER_PAGE }}
              showTotalEntries={false}
              noEndBorder
              loading={loading}
              licenseRequired
            />
          </MDBox>
        </Card>
      </MDBox>

      <DeleteModal
        open={deleteData.open}
        title={ModalContent.DPR_KPI_DELETE_TITLE}
        message={ModalContent.DPR_KPI_DELETE_MESSAGE}
        handleClose={handleCloseDeleteModal}
        handleDelete={handleDeleteKpi}
      />

      <NewProject
        openNewProject={modalOpen}
        handleCloseNewProject={handleCloseModal}
        handleSave={handleSave}
        title={`${modalType === Common.UPDATE ? "Edit" : "Add"} KPI`}
        actionButton={modalType === Common.UPDATE ? ButtonTitles.SAVE : ButtonTitles.ADD}
      >
        <Grid container spacing={2} pt={2}>
          <Grid item xs={12} sm={6}>
            <FTextField
              label="Name"
              name="name"
              placeholder={Constants.ENTER_KPI_NAME}
              value={form.name}
              handleChange={(e) => setForm({ ...form, name: e.target.value })}
              error={Boolean(errors.name)}
              helperText={errors.name}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FTextField
              label="Sort Order"
              name="sortOrder"
              type="number"
              placeholder={Constants.ENTER_SORT_ORDER}
              value={form.sortOrder}
              handleChange={(e) => setForm({ ...form, sortOrder: e.target.value })}
            />
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  color="info"
                  checked={form.isActive}
                  onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
                />
              }
              label={Constants.ACTIVE_LABEL}
            />
          </Grid>
        </Grid>
      </NewProject>
    </DashboardLayout>
  );
}

export default DprKpiConfiguration;
